// Refactored from https://github.com/Azure-Samples/ms-identity-javascript-react-tutorial/blob/main/1-Authentication/1-sign-in/SPA/src/authConfig.js

import { IPublicClientApplication } from "@azure/msal-browser";
import { waitForTeamsInit, getTeamsAuthToken } from "./teamsInit";

const appServicesAuthTokenRefreshUrl = ".auth/refresh";
const appServicesAuthLogoutUrl = ".auth/logout?post_logout_redirect_uri=/";

interface AppServicesToken {
    id_token: string;
    access_token: string;
    user_claims: Record<string, unknown>;
    expires_on: string;
}

interface TeamsSsoToken {
    access_token: string;
    user_claims: Record<string, unknown>;
    expires_on: string;
}

interface AuthSetup {
    // Set to true if login elements should be shown in the UI
    useLogin: boolean;
    // Set to true if access control is enforced by the application
    requireAccessControl: boolean;
    // Set to true if the application allows unauthenticated access (only applies for documents without access control)
    enableUnauthenticatedAccess: boolean;
    /**
     * Configuration object to be passed to MSAL instance on creation.
     * For a full list of MSAL.js configuration parameters, visit:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
     */
    msalConfig: {
        auth: {
            clientId: string; // Client app id used for login
            authority: string; // Directory to use for login https://learn.microsoft.com/entra/identity-platform/msal-client-application-configuration#authority
            redirectUri: string; // Points to window.location.origin. You must register this URI on Azure Portal/App Registration.
            postLogoutRedirectUri: string; // Indicates the page to navigate after logout.
            navigateToLoginRequestUrl: boolean; // If "true", will navigate back to the original request location before processing the auth code response.
        };
        cache: {
            cacheLocation: string; // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
            storeAuthStateInCookie: boolean; // Set this to "true" if you are having issues on IE11 or Edge
        };
    };
    loginRequest: {
        /**
         * Scopes you add here will be prompted for user consent during sign-in.
         * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
         * For more information about OIDC scopes, visit:
         * https://learn.microsoft.com/entra/identity-platform/permissions-consent-overview#openid-connect-scopes
         */
        scopes: Array<string>;
    };
    tokenRequest: {
        scopes: Array<string>;
    };
}

// Fetch the auth setup JSON data from the API if not already cached
async function fetchAuthSetup(): Promise<AuthSetup> {
    const response = await fetch("/auth_setup");
    if (!response.ok) {
        throw new Error(`auth setup response was not ok: ${response.status}`);
    }
    return await response.json();
}

const authSetup = await fetchAuthSetup();

export const useLogin = authSetup.useLogin;

export const requireAccessControl = authSetup.requireAccessControl;

export const enableUnauthenticatedAccess = authSetup.enableUnauthenticatedAccess;

export const requireLogin = requireAccessControl && !enableUnauthenticatedAccess;

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = authSetup.msalConfig;

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://learn.microsoft.com/entra/identity-platform/permissions-consent-overview#openid-connect-scopes
 */
export const loginRequest = authSetup.loginRequest;

const tokenRequest = authSetup.tokenRequest;

// Build an absolute redirect URI using the current window's location and the relative redirect URI from auth setup
export const getRedirectUri = () => {
    return window.location.origin + authSetup.msalConfig.auth.redirectUri;
};

// Cache the app services token if it's available
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/this#global_context
declare global {
    var cachedAppServicesToken: AppServicesToken | null;
    var cachedTeamsSsoToken: TeamsSsoToken | null;
}
globalThis.cachedAppServicesToken = null;
globalThis.cachedTeamsSsoToken = null;

/**
 * Retrieves an access token if the user is logged in using app services authentication.
 * Checks if the current token is expired and fetches a new token if necessary.
 * Returns null if the app doesn't support app services authentication.
 *
 * @returns {Promise<AppServicesToken | null>} A promise that resolves to an AppServicesToken if the user is authenticated, or null if authentication is not supported or fails.
 */

const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
const TEAMS_HINT_STORAGE_KEY = "teams_webview_hint";
const setTeamsHint = (): void => {
    try {
        window.sessionStorage.setItem(TEAMS_HINT_STORAGE_KEY, "1");
    } catch {
        // Ignore storage access issues
    }
};
const hasPersistedTeamsHint = (): boolean => {
    try {
        return window.sessionStorage.getItem(TEAMS_HINT_STORAGE_KEY) === "1";
    } catch {
        return false;
    }
};
const hasTeamsReferrer = (): boolean => {
    const referrer = (document.referrer || "").toLowerCase();
    return (
        referrer.includes("teams.microsoft.com") ||
        referrer.includes("teams.cloud.microsoft") ||
        referrer.includes("teams.microsoft.us") ||
        referrer.includes("teams.microsoft.cn")
    );
};
const isEmbeddedFrame = (): boolean => {
    try {
        return window.self !== window.top;
    } catch {
        return true;
    }
};
const hasTeamsUserAgent = (): boolean => {
    const ua = (navigator.userAgent || "").toLowerCase();
    return ua.includes("msteams") || ua.includes("teams/");
};
const shouldPreferTeamsSso = (): boolean => {
    const explicitTeams = hasPersistedTeamsHint() || hasTeamsReferrer();
    if (explicitTeams) {
        setTeamsHint();
        return true;
    }
    return isEmbeddedFrame() && hasTeamsUserAgent();
};

const decodeJwtClaims = (token: string): Record<string, unknown> | null => {
    try {
        const payload = token.split(".")[1];
        if (!payload) return null;
        const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
        const paddedPayload = normalizedPayload + "=".repeat((4 - (normalizedPayload.length % 4)) % 4);
        return JSON.parse(atob(paddedPayload)) as Record<string, unknown>;
    } catch {
        return null;
    }
};

const isTokenStillValid = (expiresOnIso: string): boolean => {
    const expiresOnDate = new Date(expiresOnIso);
    if (Number.isNaN(expiresOnDate.getTime())) {
        return false;
    }
    // Keep a small safety window to avoid using an about-to-expire token.
    return expiresOnDate.getTime() - 60_000 > Date.now();
};

const getUsernameFromClaims = (claims?: Record<string, unknown>): string | null => {
    if (!claims) {
        return null;
    }
    const preferredUsername = claims.preferred_username;
    if (typeof preferredUsername === "string" && preferredUsername.length > 0) {
        return preferredUsername;
    }
    const email = claims.email;
    if (typeof email === "string" && email.length > 0) {
        return email;
    }
    const upn = claims.upn;
    if (typeof upn === "string" && upn.length > 0) {
        return upn;
    }
    const uniqueName = claims.unique_name;
    if (typeof uniqueName === "string" && uniqueName.length > 0) {
        return uniqueName;
    }
    const displayName = claims.name;
    if (typeof displayName === "string" && displayName.length > 0) {
        return displayName;
    }
    return null;
};

// Teams SDK init is driven by teamsContext.tsx (in a React useEffect, after the DOM
// is ready and the Teams Desktop bridge is injected). We just wait for its result here.
const getTeamsSsoToken = async (): Promise<TeamsSsoToken | null> => {
    if (globalThis.cachedTeamsSsoToken && isTokenStillValid(globalThis.cachedTeamsSsoToken.expires_on)) {
        return globalThis.cachedTeamsSsoToken;
    }

    const inTeams = await waitForTeamsInit();
    if (!inTeams) {
        return null;
    }

    try {
        const token = await getTeamsAuthToken();
        if (!token) {
            return null;
        }

        const claims = decodeJwtClaims(token) ?? {};
        const exp = claims.exp;
        const expiresOnDate =
            typeof exp === "number" && Number.isFinite(exp)
                ? new Date(exp * 1000)
                : new Date(Date.now() + 5 * 60 * 1000);

        const teamsToken: TeamsSsoToken = {
            access_token: token,
            user_claims: claims,
            expires_on: expiresOnDate.toISOString()
        };

        globalThis.cachedTeamsSsoToken = teamsToken;
        return teamsToken;
    } catch {
        return null;
    }
};

const getAppServicesToken = (): Promise<AppServicesToken | null> => {

    if (isLocalhost) {
        return Promise.resolve(null);
    }

    if (shouldPreferTeamsSso()) {
        return Promise.resolve(null);
    }

    const checkNotExpired = (appServicesToken: AppServicesToken) => isTokenStillValid(appServicesToken.expires_on);

    if (globalThis.cachedAppServicesToken && checkNotExpired(globalThis.cachedAppServicesToken)) {
        return Promise.resolve(globalThis.cachedAppServicesToken);
    }

    const getAppServicesTokenFromMe: () => Promise<AppServicesToken | null> = () => {
        // Easy Auth is not used - App Service is set to "Allow unauthenticated access".
        // Skip the /.auth/me call to avoid a unnecessary 401 roundtrip.
        return Promise.resolve(null);
    };

    return waitForTeamsInit(2000)
        .then((inTeams: boolean) => {
            // In Teams tabs we prefer Teams SSO and avoid noisy /.auth/me 401 calls.
            if (inTeams) {
                return null;
            }
            return getAppServicesTokenFromMe();
        })
        .then((token: AppServicesToken | null) => {
        if (token) {
            if (checkNotExpired(token)) {
                globalThis.cachedAppServicesToken = token;
                return token;
            }

            return fetch(appServicesAuthTokenRefreshUrl, { redirect: "manual" })
                .then(r => {
                    if (r.ok && r.type !== "opaqueredirect") {
                        return getAppServicesTokenFromMe();
                    }
                    return null;
                })
                .catch(() => null);
        }

            return null;
        });
};

// Sign out of app services
// Learn more at https://learn.microsoft.com/azure/app-service/configure-authentication-customize-sign-in-out#sign-out-of-a-session
export const appServicesLogout = () => {
    window.location.href = appServicesAuthLogoutUrl;
};

/**
 * Determines if the user is logged in either via the MSAL public client application or the app services login.
 * @param {IPublicClientApplication | undefined} client - The MSAL public client application instance, or undefined if not available.
 * @returns {Promise<boolean>} A promise that resolves to true if the user is logged in, false otherwise.
 */
export const checkLoggedIn = async (client: IPublicClientApplication | undefined): Promise<boolean> => {
    if (client) {
        const activeAccount = client.getActiveAccount();
        if (activeAccount) {
            return true;
        }
    }

    const teamsSsoToken = await getTeamsSsoToken();
    if (teamsSsoToken) {
        return true;
    }

    const appServicesToken = await getAppServicesToken();
    if (appServicesToken) {
        return true;
    }

    return false;
};

// Get an access token for use with the API server.
// ID token received when logging in may not be used for this purpose because it has the incorrect audience
// Use the access token from app services login if available
export const getToken = async (client: IPublicClientApplication): Promise<string | undefined> => {
    const teamsSsoToken = await getTeamsSsoToken();
    if (teamsSsoToken) {
        return teamsSsoToken.access_token;
    }

    const appServicesToken = await getAppServicesToken();
    if (appServicesToken) {
        return Promise.resolve(appServicesToken.access_token);
    }

    const activeAccount = client.getActiveAccount();
    if (!activeAccount) {

        return undefined; 
    }

    return client
        .acquireTokenSilent({
            ...tokenRequest,
            account: activeAccount,
            redirectUri: getRedirectUri()
        })
        .then(r => r.accessToken)
        .catch(error => {
            return undefined;
        });
};

/**
 * Retrieves the username of the active account.
 * If no active account is found, attempts to retrieve the username from the app services login token if available.
 * @param {IPublicClientApplication} client - The MSAL public client application instance.
 * @returns {Promise<string | null>} The username of the active account, or null if no username is found.
 */
export const getUsername = async (client: IPublicClientApplication): Promise<string | null> => {
    const activeAccount = client.getActiveAccount();
    if (activeAccount) {
        return activeAccount.username;
    }

    const teamsSsoToken = await getTeamsSsoToken();
    if (teamsSsoToken?.user_claims) {
        const teamsUsername = getUsernameFromClaims(teamsSsoToken.user_claims);
        if (teamsUsername) {
            return teamsUsername;
        }
    }

    const appServicesToken = await getAppServicesToken();
    if (appServicesToken?.user_claims) {
        const appServicesUsername = getUsernameFromClaims(appServicesToken.user_claims);
        if (appServicesUsername) {
            return appServicesUsername;
        }
    }

    return null;
};

/**
 * Retrieves the token claims of the active account.
 * If no active account is found, attempts to retrieve the token claims from the app services login token if available.
 * @param {IPublicClientApplication} client - The MSAL public client application instance.
 * @returns {Promise<Record<string, unknown> | undefined>} A promise that resolves to the token claims of the active account, the user claims from the app services login token, or undefined if no claims are found.
 */
export const getTokenClaims = async (client: IPublicClientApplication): Promise<Record<string, unknown> | undefined> => {
    const activeAccount = client.getActiveAccount();
    if (activeAccount) {
        const mergedClaims: Record<string, unknown> = { ...(activeAccount.idTokenClaims ?? {}) };

        if (!mergedClaims.name && typeof activeAccount.name === "string" && activeAccount.name.length > 0) {
            mergedClaims.name = activeAccount.name;
        }
        if (typeof activeAccount.username === "string" && activeAccount.username.length > 0) {
            if (!mergedClaims.preferred_username) {
                mergedClaims.preferred_username = activeAccount.username;
            }
            if (!mergedClaims.email) {
                mergedClaims.email = activeAccount.username;
            }
        }

        if (!mergedClaims.name) {
            try {
                const accessToken = await getToken(client);
                if (accessToken) {
                    const payload = accessToken.split(".")[1];
                    const decoded = JSON.parse(atob(payload)) as Record<string, unknown>;
                    if (typeof decoded.name === "string" && decoded.name.length > 0) {
                        mergedClaims.name = decoded.name;
                    }
                }
            } catch {
                // Access token decode failed
            }
        }

        return mergedClaims;
    }

    const teamsSsoToken = await getTeamsSsoToken();
    if (teamsSsoToken) {
        return teamsSsoToken.user_claims;
    }

    const appServicesToken = await getAppServicesToken();
    if (appServicesToken) {
        return appServicesToken.user_claims;
    }

    return undefined;
};
